import { makePromise, execute } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import { HttpLink } from 'apollo-link-http'
import { WebSocketLink } from 'apollo-link-ws'
import { getMainDefinition } from 'apollo-utilities'
import { SubscriptionClient } from 'subscriptions-transport-ws'

const HTTP_BATCH_INTERVAL_MS = 30
const HTTP_BATCH_MAX = 5

function errorToString(error) {
  return `${error.path}: ${error.message}`
}

export class GraphQLError extends Error {
  constructor(errors) {
    const message =
      errors.length === 1
        ? errors[0].message
        : `Several errors happened:\n${errors
            .map(error => `* ${errorToString(error)}`)
            .join('\n')}`
    super(message)
    this.message = message
    this.errors = errors
  }
}

export class GraphQLClient {
  constructor({ config, getApiEndpoint, getApiToken }) {
    this.config = config
    this.getApiToken = getApiToken
    this.getApiEndpoint = getApiEndpoint

    this.linkWithBatching = GraphQLClient.makeHTTPLink({
      config,
      getApiToken,
      getApiEndpoint,
    })

    this.linkWithoutBatching = GraphQLClient.makeHTTPLink({
      config,
      getApiToken,
      getApiEndpoint,
      batch: false,
    })

    if (getApiToken()) {
      this.subscriptionClient = GraphQLClient.makeSubscriptionsClient({
        config,
        getApiToken,
      })

      this.wsLink = new WebSocketLink(this.subscriptionClient)
    } else {
      this.link = this.httpLink
    }
  }

  getLink = ({ query, batch = true } = {}) => {
    if (queryIsSubscription({ query })) {
      return this.wsLink
    }

    return batch ? this.linkWithBatching : this.linkWithoutBatching
  }

  query = ({ query, variables, batch = true }) => {
    return makePromise(
      execute(this.getLink({ query, batch }), {
        query,
        variables,
      })
    ).then(GraphQLClient.handleGraphQLErrors)
  }

  mutate({ query, variables, batch = true }) {
    return makePromise(
      execute(this.getLink({ query, batch }), {
        query,
        variables,
      })
    ).then(GraphQLClient.handleGraphQLErrors)
  }

  subscribe({ query, variables }) {
    return execute(this.getLink({ query }), {
      query,
      variables,
    })
  }

  static handleGraphQLErrors(data) {
    if (data.errors && data.errors.length > 0) {
      throw new GraphQLError(data.errors)
    }
    return data
  }

  static makeSubscriptionsClient({ config, getApiToken }) {
    const subscriptionClient = new SubscriptionClient(
      `${config.graphqlSubscriptionsEndpoint}`,
      {
        // 60 seconds
        timeout: 60000,
        reconnect: true,
        connectionParams: () => ({
          authorization: getApiToken(),
        }),
      }
    )

    subscriptionClient.use([
      {
        applyMiddleware(operationOptions, next) {
          operationOptions['authorization'] = getApiToken()
          next()
        },
      },
    ])

    return subscriptionClient
  }

  static makeHTTPLink({ config, getApiToken, getApiEndpoint, batch = true }) {
    /* Allow cors requests to include credentials (cookies in our case).
    ℹ️ Useful for webpack dev
    */
    /** @type RequestCredentials */
    const credentials = 'include'

    let Link = HttpLink
    let extraOptions = {}

    if (batch) {
      Link = BatchHttpLink
      extraOptions = {
        batchMax: HTTP_BATCH_MAX,
        batchInterval: HTTP_BATCH_INTERVAL_MS,
      }
    }

    return new Link({
      ...extraOptions,
      uri: `${getApiEndpoint()}/graphql`,
      fetch: function fetchWithToken(uri, options) {
        const token = getApiToken()
        const newOptions = token
          ? {
              ...options,
              headers: {
                ...(options.headers || {}),
                authorization: `JWT ${token}`,
                'x-apollo-client-name': config.appName,
                'x-apollo-client-version': config.appVersion,
              },
              mode: 'cors',
            }
          : options

        return fetch(uri, { ...newOptions, credentials })
      },
    })
  }
}

function queryIsSubscription({ query }) {
  const { kind, operation } = getMainDefinition(query)
  return kind === 'OperationDefinition' && operation === 'subscription'
}
